import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Check, Plus, X, Target } from "react-feather";
import { useForm } from "react-hook-form";
import { addMission, deleteMission, getallmission } from "../../api/missionAPI";
import TextField from "../../components/UI/TextField";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/UI/Spinner";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { CalculateLatLngDistance, capitalize } from "../../helpers/commonFunction";
import { useMapStore } from "../../store/mapStore";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { CloudUpload, Edit, Save, TrashCan } from "@carbon/icons-react";
import LabeledTextField from "../../components/UI/LabeledTextField";
import EditWaypoint from "./editWaypoint";
import { Upload } from "react-feather";
import MissionPlanning from "./missionModal/missionPlanning";
import { missionCategory } from "../../helpers/constants";
// import Payment from "../../components/payment";
// import xml2js from "xml2js"

const MissionPage = () => {
  // get auth token
  const [searchMission, setSearchMission] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [missionData, setMissionData] = useState([]);
  const { token, setLoaderState, isPilot } = useAuthStore((state) => state);
  const { setMissionPlanningWaypoints, setShowMissionPlanningPopup, showMissionPlanningPopup } = useMapStore((state) => state);
  const navigate = useNavigate();
  const { isLoading, data } = useQuery({
    queryFn: getallmission,
    queryKey: ["getMissions", token],
    refetchOnWindowFocus: false,
  });
  const qClient = useQueryClient();

  // Add mission mutation
  const MissionQuery = useMutation({
    mutationFn: addMission,
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: "getMissions" });
    },
  });

  // Delete mission mutation
  const deleteMissionQuery = useMutation({
    mutationFn: deleteMission,
    onSuccess: () => {
      qClient.invalidateQueries({ queryKey: "getMissions" });
    },
  });

  const deleteMissionFunc = (missionId) => {
    deleteMissionQuery.mutate({ missionId, token });
  };

  // Form hooks
  const { register, handleSubmit, getValues, reset } = useForm();
  const waypointForm = useForm();
  const rallypointForm = useForm();

  // Waypoints state
  const [waypoints, setWaypoints] = useState([]);
  const [missionDistance, setMissionPlanningDistance] = useState(0);
  const [rallyWaypoints, setRallyWaypoints] = useState([]);
  const [isAdding, setAdding] = useState(false);

  // Calculate distance
  const calculateDistance = (latLngArray) => {
    let distance = 0;
    if (latLngArray?.length > 0) {
      for (let index = 0; index < latLngArray?.length - 1; index++) {
        distance += CalculateLatLngDistance(latLngArray[index]?.lat, latLngArray[index]?.lng, latLngArray[index + 1]?.lat, latLngArray[index + 1]?.lng);
      }
    }
    setMissionPlanningDistance(distance);
  };

  // Add waypoint
  const addWaypoint = () => {
    const waypoint = waypointForm.getValues();
    setWaypoints([...waypoints, waypoint]);
    waypointForm.resetField("lat");
    waypointForm.resetField("lng");
  };

  // Add rally waypoint
  const addRallyWaypoint = () => {
    const rallyWaypoint = rallypointForm.getValues();
    setRallyWaypoints([...rallyWaypoints, rallyWaypoint]);
    rallypointForm.resetField("lat");
    rallypointForm.resetField("lng");
  };

  // Submit mission
  const submitMission = () => {
    let payload = getValues();
    payload = { ...payload, waypoints, rallyWaypoints };
    if (token && payload) {
      if (waypoints.length < 2) {
        toast.error("Add atleast 2 waypoints ");
        return;
      }
      for (let i = 0; i < waypoints?.length; i++) {
        if (waypoints[i].alt > 120) {
          toast.error("Altitude above 120 meters not permitted.");
          return;
        }
      }
      MissionQuery.mutate({ token, payload });
      waypointForm.reset();
      rallypointForm.reset();
      setWaypoints([]);
      setRallyWaypoints([]);
      setAdding(false);
    }
  };

  // Handle mission details page navigation
  const missionDetailsPage = (mission) => {
    navigate(`/dashboard/mission-details/${mission._id}`);
  };

  // Handle file change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const kmlData = event.target.result;
        parseKML(kmlData);
      };
      reader.readAsText(file);
    }
  };

  // Parse KML file
  const parseKML = (kmlData) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlData, "text/xml");
    const placemarks = xmlDoc.getElementsByTagName("Placemark");
    const parsedData = [];

    for (let i = 0; i < placemarks.length; i++) {
      const name = placemarks[i].getElementsByTagName("name")[0].textContent;
      const coordinates = placemarks[i].getElementsByTagName("coordinates")[0].textContent;
      const coordinatesArray = coordinates.split(",").map((coord) => parseFloat(coord));
      parsedData.push({ name, coordinates: coordinatesArray });
    }
    const parsedDataWaypoints = parsedData.map((waypoint) => {
      return { lat: waypoint.coordinates[1], lng: waypoint.coordinates[0], alt: 120 };
    });
    setWaypoints(parsedDataWaypoints);
    return parsedData;
  };

  // Handle drag end for waypoints
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedWaypoints = Array.from(waypoints);
    const [removed] = reorderedWaypoints.splice(result.source.index, 1);
    reorderedWaypoints.splice(result.destination.index, 0, removed);
    setWaypoints(reorderedWaypoints);
  };

  // Handle drag end for rally waypoints
  const handleDragEndRallyWaypoints = (result) => {
    if (!result.destination) return;
    const reorderedWaypoints = Array.from(rallyWaypoints);
    const [removed] = reorderedWaypoints.splice(result.source.index, 1);
    reorderedWaypoints.splice(result.destination.index, 0, removed);
    setRallyWaypoints(reorderedWaypoints);
  };

  // Update mission data on query result
  useEffect(() => {
    setMissionData(data?.data?.data);
  }, [data?.data?.data]);

  // Update filtered data on search input change
  useEffect(() => {
    if (searchMission === "") {
      setFilteredData(missionData);
    } else {
      const regex = new RegExp(searchMission, "i");
      const filtered = missionData?.filter((mission) => regex.test(mission?.missionName));
      setFilteredData(filtered);
    }
  }, [searchMission, missionData]);

  useEffect(() => {
    setMissionPlanningWaypoints(waypoints)
  }, [waypoints])

  return (
    <div className="p-4">
      <ToastContainer />
      {showMissionPlanningPopup && <MissionPlanning setWaypoints={setWaypoints} />}
      <div className="flex justify-between items-center">
        <div className="flex">
          <h1 className="text-4xl h-full">My Missions</h1>
          {!isAdding && (
            <div className="max-w-md mx-auto border rounded-lg ml-3">
              <div className="relative flex items-center w-full h-12 rounded-lg focus-within:shadow-lg bg-white overflow-hidden">
                <div className="grid place-items-center h-full w-12 text-gray-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  className="peer h-full w-full outline-none text-sm text-gray-700 pr-2"
                  onChange={(e) => setSearchMission(e.target.value)}
                  type="text"
                  id="search"
                  placeholder="Search Mission..."
                />
              </div>
            </div>
          )}
        </div>

        {isAdding ? (
          <div className="flex items-center">
            <input type="file" id="uploadKmlFile" onChange={handleFileChange} className="hidden" />
            <label htmlFor="uploadKmlFile" title="Upload Kml file" className="mx-3 bg-tblu p-4 rounded-xl text-white">
              <div className="flex w-[8vw] items-center justify-center">
                <div>
                  <Upload className="cursor-pointer" />
                </div>
                <div className="ml-3">Upload KML</div>
              </div>
            </label>
            <button onClick={handleSubmit(submitMission)} className="bg-tblu flex text-white whitespace-nowrap p-4 text-md rounded-xl">
              <Check /> &ensp; Save
            </button>
            <button
              onClick={() => {
                setAdding(false);
                setMissionPlanningWaypoints(null);
                setWaypoints([]);
                reset();
                waypointForm.reset();
              }}
              className="cancel p-4 text-md rounded-xl text-white bg-red-400 ml-4"
            >
              <X />
            </button>
          </div>
        ) : (
          <>
            {!isPilot && (
              <button onClick={() => setAdding(true)} className="bg-tblu flex text-white whitespace-nowrap p-4 text-md rounded-xl">
                <Plus /> &ensp; Add mission
              </button>
            )}
          </>
        )}
      </div>
      {isAdding && (
        <form onSubmit={(e) => e.preventDefault()} className="mt-4">
          <div className="grid grid-cols-2 gap-5">
            <div className="grid-cols-1">
              <TextField placeholder={"Add a name"} label="Mission Name*" inputProps={{ ...register("missionName", { required: true }) }} />
            </div>
            <div className="grid-cols-1">
              <span>
                <h2 className="text-tblu">Mission Type*</h2>
                <select
                  {...register("missionType", { required: true })}
                  className="w-full my-2 block p-4 border-slate-200 border rounded-xl focus:outline-none "
                >
                  {missionCategory.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          </div>
          <h1 className="text-xl mb-4">Add Waypoints</h1>
          <div className="waypoint-form">
            <div className="added-points flex flex-wrap">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="waypoint">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {waypoints?.map((point, i) => (
                        <div className="flex items-center">
                          <div>
                            {i === 0 ? (
                              <img src="https://icon-library.com/images/map-marker-icon/map-marker-icon-18.jpg" className="h-10" alt="" />
                            ) : i === waypoints?.length - 1 ? (
                              <img src="https://icon-library.com/images/icon-markers/icon-markers-4.jpg" className="h-10" alt="" srcSet="" />
                            ) : (
                              <img src="https://icon-library.com/images/google-map-marker-icon/google-map-marker-icon-9.jpg" className="h-10" />
                            )}
                          </div>
                          <Draggable key={`waypoint-${i}`} draggableId={`waypoint-${i}`} index={i}>
                            {(provided) => <EditWaypoint provided={provided} point={point} setWaypoints={setWaypoints} index={i} />}
                          </Draggable>
                        </div>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div className="adder flex justify-between items-center">
              <div className="flex">
                <TextField placeholder={"Latitude"} label="Latitude*" inputProps={{ ...waypointForm.register("lat", { required: true }) }} />
                &ensp;
                <TextField placeholder={"Longitude"} label="Longitude*" inputProps={{ ...waypointForm.register("lng", { required: true }) }} />
                &ensp;
                <TextField placeholder={"Altitude"} label="Altitude*" inputProps={{ ...waypointForm.register("alt", { required: true }) }} />
              </div>
              <button onClick={waypointForm.handleSubmit(addWaypoint)} className="rounded-xl bg-tblu p-4 text-white mt-6">
                <Plus />
              </button>
            </div>
          </div>

          <h1 className="my-2 font-bold">Total Distance :-{missionDistance.toFixed(2)} km</h1>
          <h1 className="text-xl mb-4">Add Rally Waypoints</h1>
          <div className="waypoint-form">
            <div className="added-points flex flex-wrap">
              <DragDropContext onDragEnd={handleDragEndRallyWaypoints}>
                <Droppable droppableId="waypoint">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {rallyWaypoints?.map((point, i) => (
                        <div className="flex items-center">
                          <div>
                            {i === 0 ? (
                              <img src="https://icon-library.com/images/map-marker-icon/map-marker-icon-18.jpg" className="h-10" alt="" />
                            ) : i === rallyWaypoints?.length - 1 ? (
                              <img src="https://icon-library.com/images/icon-markers/icon-markers-4.jpg" className="h-10" alt="" srcSet="" />
                            ) : (
                              <img src="https://icon-library.com/images/google-map-marker-icon/google-map-marker-icon-9.jpg" className="h-10" />
                            )}
                          </div>
                          <Draggable key={`waypoint-${i}`} draggableId={`waypoint-${i}`} index={i}>
                            {(provided) => <EditWaypoint provided={provided} point={point} setWaypoints={setRallyWaypoints} index={i} />}
                          </Draggable>
                        </div>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <div className="adder flex justify-between items-center">
              <div className="flex">
                <TextField placeholder={"Latitude"} label="Latitude*" inputProps={{ ...rallypointForm.register("lat", { required: true }) }} />
                &ensp;
                <TextField placeholder={"Longitude"} label="Longitude*" inputProps={{ ...rallypointForm.register("lng", { required: true }) }} />
                &ensp;
                <TextField placeholder={"Altitude"} label="Altitude*" inputProps={{ ...rallypointForm.register("alt", { required: true }) }} />
              </div>
              <button onClick={rallypointForm.handleSubmit(addRallyWaypoint)} className="rounded-xl bg-tblu p-4 text-white mt-6">
                <Plus />
              </button>
            </div>
          </div>
        </form>
      )}
      <br />
      <hr />
      <br />
      <div className="drones-list">
        {isLoading && <Spinner />}
        {data?.data?.data?.length === 0 ? (
          <div className="flex w-full justify-center">
            <span className="text-slate-400 text-xl">No Missions Added</span>
          </div>
        ) : (
          filteredData?.map((mission, index) => (
            <div key={index} className="drone-card cursor-pointer bg-white p-4 border rounded-xl my-2 flex justify-between items-center">
              <div className="flex-grow" onClick={() => missionDetailsPage(mission)}>
                <div className="flex">
                  <Target size={32} className="text-tblu mr-2" />
                  <h1 className="text-xl capitalize">{capitalize(mission?.missionName)}</h1>
                </div>
              </div>
              <div className="items-center" onClick={() => deleteMissionFunc(mission._id)}>
                <TrashCan size={20} className="text-red-500" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MissionPage;
